import { Skeleton } from '@doccareplus/ui';

/**
 * Loading placeholders for the clinic discovery feature. Each is a
 * `role="status"` region (with an sr-only label) wrapping `aria-hidden`
 * shimmer primitives, so assistive tech announces "loading" once instead of
 * reading every block. Shapes mirror the real layouts to avoid a jump when
 * data arrives.
 */

function LoadingRegion({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div role="status" aria-label={label}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** A single previous-request row: title line + meta line + a status pill. */
function RequestRowSkeleton() {
  return (
    <div
      className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-3.5 shadow-[var(--shadow-1)]"
      aria-hidden
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-3.5 w-2/5" />
          <Skeleton className="h-3 w-1/3" />
        </div>
        <Skeleton className="h-5 w-16 rounded-full" />
      </div>
    </div>
  );
}

/** The "Your open clinic requests" section — a short stacked list. */
export function ClinicRequestsListSkeleton({ label, rows = 2 }: { label: string; rows?: number }) {
  return (
    <LoadingRegion label={label}>
      <div className="flex flex-col gap-2">
        {Array.from({ length: rows }).map((_, i) => (
          <RequestRowSkeleton key={i} />
        ))}
      </div>
    </LoadingRegion>
  );
}

/** A single clinic card: name/location lines and a full-width action button. */
function ClinicCardSkeleton() {
  return (
    <div
      className="rounded-[14px] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-1)]"
      aria-hidden
    >
      <Skeleton className="h-4 w-3/5" />
      <Skeleton className="mt-2 h-3 w-2/5" />
      <Skeleton className="mt-1.5 h-3 w-1/4" />
      <Skeleton className="mt-4 h-9 w-full rounded-[9px]" />
    </div>
  );
}

/** The clinic results grid — mirrors the real `grid gap-3 md:grid-cols-2` layout. */
export function ClinicResultsGridSkeleton({ label, cards = 4 }: { label: string; cards?: number }) {
  return (
    <LoadingRegion label={label}>
      <div className="grid gap-3 md:grid-cols-2">
        {Array.from({ length: cards }).map((_, i) => (
          <ClinicCardSkeleton key={i} />
        ))}
      </div>
    </LoadingRegion>
  );
}

/** A row of slot-chip placeholders (provider / service / time options). */
export function SlotOptionsSkeleton({ label, rows = 2, chips = 4 }: { label: string; rows?: number; chips?: number }) {
  return (
    <LoadingRegion label={label}>
      <div className="flex flex-col gap-4">
        {Array.from({ length: rows }).map((_, r) => (
          <div key={r}>
            <Skeleton className="mb-2 h-3 w-24" aria-hidden />
            <div className="flex flex-wrap gap-2" aria-hidden>
              {Array.from({ length: chips }).map((_, c) => (
                <Skeleton key={c} className="h-8 w-20 rounded-[9px]" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </LoadingRegion>
  );
}
